"use client"

import {
    type FieldError,
    type FieldErrorCode,
    type FieldValidator
} from "@/app/(site)/_components/Atoms/Inputs/errors/fieldErrorCodes"
import { useFieldValidation } from "@/app/(site)/_components/Atoms/Inputs/errors/useFieldValidation"
import IconChevronDown from "@/app/(site)/_components/Atoms/Icons/IconChevronDown"
import { INPUT_DEFAULT_HEIGHT } from "@/app/(site)/_components/Atoms/Inputs/Input"
import { fieldValueTextClasses } from "@/app/(site)/_components/Atoms/Inputs/input-text-classes"
import InputErrors from "@/app/(site)/_components/Atoms/Inputs/InputErrors"
import { Skeleton } from "@/app/(site)/_components/Atoms/Skeleton"
import { type FontStylesWithoutDot } from "@/config/fontConfig/fontStyles"
import { FOCUS_VISIBLE_BORDER_CLASS } from "@/src/utils/a11y/focusVisible"
import { clsx } from "@/src/utils/clsx"
import { useEffect, useState } from "react"

interface SelectProps {
    defaultValue?: string
    value?: string
    disabled?: boolean
    isLoading?: boolean
    label?: string
    name: string
    required?: boolean
    langCode: string
    onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void
    labelClassName?: string
    inputClassName?: string
    typography?: FontStylesWithoutDot
    labelTypography?: FontStylesWithoutDot
    containerClassName?: string
    customValidators?: FieldValidator[]
    /** Decided elsewhere — a PFAS restriction, say — rather than by a check here. */
    externalErrors?: Array<FieldErrorCode | FieldError>
    isFocused?: boolean
    showFloatingLabel?: boolean
    prefix?: string
    onBlurValidation?: boolean
    placeholder?: string
    options?: SelectOption[]
    onBlur?: (e: React.FocusEvent<HTMLSelectElement>) => void
    onFocus?: (e: React.FocusEvent<HTMLSelectElement>) => void
    id: string
    isCountrySelector?: boolean
    autoComplete?: string
    tabIndex?: number
}

interface SelectOption {
    value: string
    label: string
}

export default function Select({
    defaultValue = "",
    value,
    disabled = false,
    isLoading = false,
    label,
    name,
    required = false,
    langCode,
    onBlur,
    onFocus,
    onChange,
    labelClassName,
    typography = "Trade_Gothic_Text_16_150_0_300",
    labelTypography = "Trade_Gothic_Text_12_150_0_300",
    inputClassName,
    containerClassName,
    onBlurValidation = false,
    prefix = "",
    customValidators,
    externalErrors,
    placeholder = "",
    options = [],
    isCountrySelector = false,
    autoComplete = "on",
    tabIndex
}: SelectProps) {
    const [isFocused, setIsFocused] = useState(false)
    const [hasValue, setHasValue] = useState<boolean>(!!(value || defaultValue))

    const { elementRef, errors, hasErrors, errorCode, validate, clear } =
        useFieldValidation<HTMLSelectElement>({
            name,
            langCode,
            validators: customValidators,
            externalErrors,
            // A value the customer picked from a list we supplied needs no character check.
            validateCharacters: false
        })

    const errorId = `${name}-error`

    useEffect(() => {
        setHasValue(!!(value || defaultValue))
    }, [value, defaultValue])

    const showFloatingLabel = isFocused || hasValue || !!placeholder

    const handleFocus = (ev: React.FocusEvent<HTMLSelectElement>) => {
        setIsFocused(true)
        onFocus?.(ev)
    }

    const handleBlur = (ev: React.FocusEvent<HTMLSelectElement>) => {
        setIsFocused(false)
        setHasValue(!!ev.target.value)

        if (onBlurValidation) {
            validate()
        }
        onBlur?.(ev)
    }

    const handleChange = (ev: React.ChangeEvent<HTMLSelectElement>) => {
        clear()
        onChange?.(ev)
    }

    return (
        <div
            className={clsx("relative w-full", containerClassName)}
            data-error={errorCode}
        >
            <label
                htmlFor={name}
                className={clsx(
                    "relative block",
                    disabled && "cursor-not-allowed opacity-[20%]",
                    labelClassName
                )}
                aria-label={label}
                aria-required={required}
            >
                <select
                    tabIndex={tabIndex}
                    ref={elementRef}
                    id={name}
                    name={name}
                    {...(value !== undefined ? { value } : { defaultValue })}
                    disabled={disabled || isLoading}
                    onChange={handleChange}
                    onFocus={handleFocus}
                    onInvalid={e => {
                        e.preventDefault()
                        validate()
                    }}
                    onBlur={handleBlur}
                    className={clsx(
                        "w-full truncate rounded-lg border bg-col-bg-primary py-16 pb-0 pl-12 pr-32 transition-[border-color] transition-colors duration-200",
                        "[&::-webkit-appearance]:none appearance-none [&::-ms-expand]:hidden",
                        FOCUS_VISIBLE_BORDER_CLASS,
                        fieldValueTextClasses(hasValue),
                        typography,
                        `h-[${INPUT_DEFAULT_HEIGHT}px] min-h-[${INPUT_DEFAULT_HEIGHT}px]`,
                        hasErrors
                            ? `!border-col-border-alert`
                            : disabled
                              ? "cursor-not-allowed border-col-border-neutral"
                              : isFocused
                                ? "border-col-border-strong"
                                : "border-col-border-neutral hover:border-col-border-strong",
                        inputClassName
                    )}
                    required={required}
                    autoComplete={autoComplete}
                    aria-invalid={hasErrors}
                    aria-describedby={hasErrors ? errorId : undefined}
                >
                    {!isCountrySelector && (
                        <option
                            value=""
                            className={clsx("text-col-text-secondary", labelTypography)}
                        ></option>
                    )}
                    {options.map((option, index) => (
                        <option
                            key={option.value + index + option.label}
                            value={option.value}
                            className="truncate"
                        >
                            {option.label}
                        </option>
                    ))}
                </select>

                {/* Custom dropdown arrow */}
                <div
                    className={clsx(
                        "pointer-events-none absolute right-12 top-1/2 -translate-y-1/2",
                        disabled && "opacity-30"
                    )}
                    aria-label={`Select ${label ? label : ""}`}
                >
                    <IconChevronDown className="h-16 w-16 text-col-text-primary" />
                </div>

                {/* isLoadingSWR skeleton inside input */}
                {isLoading && (
                    <Skeleton className="absolute left-4 right-4 top-1/2 z-10 h-[35px] -translate-y-1/2" />
                )}
                {/* Floating Label */}
                {label && (
                    <span
                        className={clsx(
                            "pointer-events-none absolute left-13 select-none text-col-text-secondary transition-all duration-200 ease-in-out",
                            typography,
                            isLoading && "opacity-0",
                            showFloatingLabel
                                ? "top-2 origin-left scale-75"
                                : "top-1/2 -translate-y-1/2 scale-100",
                            prefix && !showFloatingLabel && "left-10"
                        )}
                    >
                        {label}
                        {required && "*"}
                    </span>
                )}
            </label>

            {hasErrors && (
                <InputErrors
                    errors={errors}
                    id={errorId}
                    isFocused={false}
                    className="pl-14 pt-6"
                />
            )}
        </div>
    )
}
